import {
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	Skeleton,
	Textarea,
} from "@ancatag/at-design";
import { Cpu, Key, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "wouter";
import { type Project, projectService } from "../services/projects";

// ═══════════════════════════════════════════════════════════════════════════
// PROJECT CARD
// ═══════════════════════════════════════════════════════════════════════════

function ProjectCard({
	project,
	onSelect,
}: {
	project: Project;
	onSelect: (id: string) => void;
}) {
	return (
		<Card
			className="hover:shadow-md transition-shadow cursor-pointer"
			onClick={() => onSelect(project.id)}
		>
			<CardHeader className="pb-3">
				<div className="flex items-start justify-between">
					<div className="flex-1 min-w-0">
						<CardTitle className="text-lg truncate">{project.name}</CardTitle>
						<CardDescription className="mt-1 line-clamp-2">
							{project.description || "No description"}
						</CardDescription>
					</div>
					<Badge variant={project.isActive ? "default" : "secondary"}>
						{project.isActive ? "Active" : "Inactive"}
					</Badge>
				</div>
			</CardHeader>
			<CardContent>
				<div className="flex items-center gap-4 text-sm text-muted-foreground">
					<div className="flex items-center gap-1">
						<Key className="h-4 w-4" />
						<span>{project._count?.apiKeys ?? 0} API Keys</span>
					</div>
					<div className="flex items-center gap-1">
						<Cpu className="h-4 w-4" />
						<span>{project._count?.modelConfigs ?? 0} Models</span>
					</div>
				</div>
				<div className="mt-4 text-xs text-muted-foreground">
					Created {new Date(project.createdAt).toLocaleDateString()}
				</div>
			</CardContent>
		</Card>
	);
}

// ═══════════════════════════════════════════════════════════════════════════
// CREATE PROJECT DIALOG
// ═══════════════════════════════════════════════════════════════════════════

function CreateProjectDialog({
	open,
	onOpenChange,
	onCreated,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onCreated: () => void;
}) {
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!name.trim()) {
			setError("Project name is required");
			return;
		}

		setSaving(true);
		setError(null);
		try {
			await projectService.create({
				name: name.trim(),
				description: description.trim() || null,
			});
			onCreated();
			onOpenChange(false);
			setName("");
			setDescription("");
		} catch (err: any) {
			setError(err.message || "Failed to create project");
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Create New Project</DialogTitle>
					<DialogDescription>
						Projects help you organize API keys and model configurations.
					</DialogDescription>
				</DialogHeader>
				<form onSubmit={handleSubmit} className="space-y-4">
					{error && (
						<div className="p-3 text-sm bg-destructive/10 text-destructive rounded-lg">
							{error}
						</div>
					)}
					<div className="space-y-2">
						<label className="text-sm font-medium">Name</label>
						<Input
							placeholder="My Project"
							value={name}
							onChange={(e) => setName(e.target.value)}
							autoFocus
						/>
					</div>
					<div className="space-y-2">
						<label className="text-sm font-medium">Description</label>
						<Textarea
							placeholder="Optional description..."
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							rows={3}
						/>
					</div>
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={saving}>
							{saving ? "Creating..." : "Create Project"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function Projects() {
	const [, setLocation] = useLocation();
	const [projects, setProjects] = useState<Project[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [search, setSearch] = useState("");

	const fetchProjects = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);
			const data = await projectService.list();
			setProjects(data);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to load projects");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchProjects();
	}, [fetchProjects]);

	const handleSelectProject = (projectId: string) => {
		// Navigate to API keys for this project
		setLocation(`/api-keys?project=${projectId}`);
	};

	const filteredProjects = projects.filter(
		(p) =>
			p.name.toLowerCase().includes(search.toLowerCase()) ||
			(p.description?.toLowerCase().includes(search.toLowerCase()) ?? false),
	);

	return (
		<div className="container mx-auto max-w-7xl py-8 px-4 sm:px-6 lg:px-8">
			<div className="flex items-center justify-between mb-6">
				<div>
					<h1 className="text-3xl font-bold text-foreground">Projects</h1>
					<p className="text-muted-foreground mt-2">
						Organize your API keys and model configurations into projects.
					</p>
				</div>
				<Button onClick={() => setDialogOpen(true)}>
					<Plus className="h-4 w-4 mr-2" />
					New Project
				</Button>
			</div>

			{error && (
				<div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
					{error}
				</div>
			)}

			{/* Search */}
			<div className="mb-6">
				<Input
					placeholder="Search projects..."
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					className="max-w-sm"
				/>
			</div>

			{/* Projects Grid */}
			{loading ? (
				<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
					{[...Array(6)].map((_, i) => (
						<Card key={i}>
							<CardHeader className="pb-3">
								<Skeleton className="h-5 w-32 mb-2" />
								<Skeleton className="h-4 w-full" />
							</CardHeader>
							<CardContent>
								<Skeleton className="h-16 w-full" />
							</CardContent>
						</Card>
					))}
				</div>
			) : filteredProjects.length === 0 ? (
				<div className="p-8 border-2 border-dashed border-border rounded-lg text-center">
					<p className="text-muted-foreground mb-4">
						{search
							? "No projects match your search."
							: "No projects yet. Create your first one!"}
					</p>
					{!search && (
						<Button onClick={() => setDialogOpen(true)}>
							<Plus className="h-4 w-4 mr-2" />
							Create Project
						</Button>
					)}
				</div>
			) : (
				<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
					{filteredProjects.map((project) => (
						<ProjectCard
							key={project.id}
							project={project}
							onSelect={handleSelectProject}
						/>
					))}
				</div>
			)}

			{/* Create Dialog */}
			<CreateProjectDialog
				open={dialogOpen}
				onOpenChange={setDialogOpen}
				onCreated={fetchProjects}
			/>
		</div>
	);
}
